(() => {
  const initializePdp = (root) => {
    if (!root || root.dataset.lnpReady === 'true') return;
    root.dataset.lnpReady = 'true';

    root.querySelectorAll('[data-lnp-gallery]').forEach((gallery) => {
      if (gallery.dataset.lnpGalleryReady === 'true') return;
      const slides = [...gallery.querySelectorAll('[data-lnp-slide]')];
      const thumbs = [...gallery.querySelectorAll('[data-lnp-target]')];
      const viewport = gallery.querySelector('.lnp-main-media');
      const controls = gallery.querySelector('[data-lnp-gallery-controls]');
      if (!viewport || slides.length < 2) return;

      const featuredIndex = gallery.dataset.lnpFeaturedMediaId
        ? slides.findIndex((slide) => slide.dataset.lnpMediaId === gallery.dataset.lnpFeaturedMediaId) : -1;
      let activeIndex = featuredIndex >= 0 ? featuredIndex : Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let gesture = null;

      const selectSlide = (index, revealThumb = true) => {
        activeIndex = (index + slides.length) % slides.length;
        const target = slides[activeIndex].dataset.lnpSlide;
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === activeIndex);
          if (i !== activeIndex) slide.querySelectorAll('video').forEach((video) => video.pause());
        });
        thumbs.forEach((thumb) => {
          const selected = thumb.dataset.lnpTarget === target;
          thumb.classList.toggle('is-active', selected);
          if (selected) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });

        // Scroll only the thumbnail strip, never the product page.
        const thumb = thumbs.find((item) => item.dataset.lnpTarget === target);
        const strip = thumb?.parentElement;
        if (revealThumb && strip && strip.scrollWidth > strip.clientWidth) {
          const box = thumb.getBoundingClientRect();
          const frame = strip.getBoundingClientRect();
          const delta = box.left < frame.left ? box.left - frame.left :
            box.right > frame.right ? box.right - frame.right : 0;
          if (delta) strip.scrollTo({
            left: strip.scrollLeft + delta,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        }
      };

      gallery.lnpSelectMedia = (mediaId) => {
        const index = slides.findIndex((slide) => slide.dataset.lnpMediaId === String(mediaId));
        if (index >= 0) selectSlide(index);
      };

      thumbs.forEach((thumb) => thumb.addEventListener('click', (event) => {
        if (gallery.dataset.lnpGalleryReady !== 'true' || event.defaultPrevented ||
            event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const index = slides.findIndex((slide) => slide.dataset.lnpSlide === thumb.dataset.lnpTarget);
        if (index < 0) return;
        event.preventDefault();
        selectSlide(index);
      }));
      controls?.querySelector('[data-lnp-previous]')?.addEventListener('click', () => selectSlide(activeIndex - 1));
      controls?.querySelector('[data-lnp-next]')?.addEventListener('click', () => selectSlide(activeIndex + 1));

      gallery.addEventListener('keydown', (event) => {
        if (event.target.closest('input, textarea, select, video, iframe, model-viewer')) return;
        const index = { ArrowLeft: activeIndex - 1, ArrowRight: activeIndex + 1, Home: 0, End: slides.length - 1 }[event.key];
        if (index === undefined) return;
        event.preventDefault();
        selectSlide(index);
      });

      viewport.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary) { gesture = null; return; }
        if (event.button !== 0 || event.target.closest('button, a, video, iframe, model-viewer') ||
            !slides[activeIndex].querySelector('.lnp-media-image')) return;
        gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, axis: null };
        viewport.setPointerCapture?.(event.pointerId);
      });
      viewport.addEventListener('pointermove', (event) => {
        if (!gesture || event.pointerId !== gesture.id || gesture.axis) return;
        const x = Math.abs(event.clientX - gesture.x);
        const y = Math.abs(event.clientY - gesture.y);
        if (Math.max(x, y) > 10) gesture.axis = x > y * 1.25 ? 'x' : 'y';
      });
      viewport.addEventListener('pointerup', (event) => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const { x, y, axis } = gesture;
        gesture = null;
        const dx = event.clientX - x;
        const dy = event.clientY - y;
        const threshold = Math.max(40, Math.min(80, viewport.clientWidth * 0.12));
        if (axis !== 'y' && Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy) * 1.25) {
          selectSlide(activeIndex + (dx < 0 ? 1 : -1));
        }
      });
      viewport.addEventListener('pointercancel', () => { gesture = null; });
      viewport.addEventListener('lostpointercapture', () => { gesture = null; });
      viewport.addEventListener('dragstart', (event) => {
        if (event.target.matches('.lnp-media-image')) event.preventDefault();
      });

      selectSlide(activeIndex, false);
      gallery.dataset.lnpGalleryReady = 'true';
      if (controls) controls.hidden = false;
    });

    const productInfo = root.closest('product-info');
    if (!productInfo) return;

    const priceContainer = productInfo.querySelector(`#price-${productInfo.dataset.section}`);
    const mainSubmit = productInfo.querySelector(`#ProductSubmitButton-${productInfo.dataset.section}`);
    const mobileSubmit = root.querySelector('[data-lnp-mobile-cart] button');

    const setTextIfChanged = (element, value) => {
      if (!element || !value || element.textContent.trim() === value) return;
      element.textContent = value;
    };

    const setAttributeIfChanged = (element, name, value) => {
      if (!element || element.getAttribute(name) === value) return;
      element.setAttribute(name, value);
    };

    const syncPurchaseState = () => {
      const customPrice = priceContainer?.querySelector('.lnp-current-price, .lnb-current-price');
      const salePrice = priceContainer?.querySelector('.price--on-sale .price-item--sale');
      const regularPrice = priceContainer?.querySelector('.price-item--regular');
      const livePrice = customPrice || salePrice || regularPrice;
      const buttonPrice = root.querySelector('[data-lnp-button-price]');
      const mobilePrice = root.querySelector('[data-lnp-mobile-price]');

      const livePriceText = livePrice?.textContent.trim();
      setTextIfChanged(buttonPrice, livePriceText);
      setTextIfChanged(mobilePrice, livePriceText);

      if (mainSubmit && mobileSubmit) {
        const isDisabled = mainSubmit.disabled || mainSubmit.getAttribute('aria-disabled') === 'true';
        const isLoading = mainSubmit.classList.contains('loading');

        if (mobileSubmit.disabled !== isDisabled) mobileSubmit.disabled = isDisabled;
        setAttributeIfChanged(mobileSubmit, 'aria-disabled', String(isDisabled));
        setAttributeIfChanged(mobileSubmit, 'aria-busy', String(isLoading));
        if (mobileSubmit.classList.contains('loading') !== isLoading) {
          mobileSubmit.classList.toggle('loading', isLoading);
        }

        const mainLabel = mainSubmit.querySelector('span')?.textContent.trim();
        const mobileLabel = mobileSubmit.querySelector('span');
        setTextIfChanged(mobileLabel, mainLabel);
      }
    };

    syncPurchaseState();

    if (priceContainer) {
      new MutationObserver(syncPurchaseState).observe(priceContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    if (mainSubmit) {
      new MutationObserver(syncPurchaseState).observe(mainSubmit, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });
    }
  };

  const boot = () => document.querySelectorAll('[data-lnp-root]').forEach(initializePdp);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
